import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Container,
  TextField,
  Button,
  Typography,
  Box,
  MenuItem,
  Grid,
  Paper,
  Alert,
  CircularProgress,
  InputAdornment,
  Divider,
  Autocomplete,
  useTheme,
  alpha
} from "@mui/material";
import { Person, AddCircleOutline, ArrowBack } from '@mui/icons-material';

const JobEntryForm = () => {
  const [form, setForm] = useState({
    client_name: "",
    client_email: "",
    client_phone: "",
    job_received_date: new Date().toISOString().slice(0, 10),
    mode_received: "",
    job_description: "",
    type_of_job: "",
    assigned_to: "",
    target_completion_date: ""
  });
  const [typeOptions, setTypeOptions] = useState([]);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [userOptions, setUserOptions] = useState([]);
  const [clientOptions, setClientOptions] = useState([]);
  const navigate = useNavigate();
  const location = useLocation();
  const user = location.state?.user;
  const FIRMS = ['Datachef', 'Techsahyogi'];
  const [firm, setFirm] = useState(location.state?.firm || 'Datachef');

  const theme = useTheme();

  const formStyles = {
    paper: {
      p: { xs: 2, sm: 4 },
      borderRadius: 3,
      boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
      background: '#fff',
      maxWidth: 600,
      mx: 'auto',
      mt: 6,
    },
    sectionTitle: {
      mt: 3,
      mb: 1,
      color: theme.palette.primary.main,
      fontWeight: 600,
      fontSize: 18,
      letterSpacing: 0.5,
    },
    field: {
      mb: 2,
      '& .MuiOutlinedInput-root': {
        background: '#fafbfc',
        borderRadius: 2,
        '&:hover fieldset': {
          borderColor: theme.palette.primary.main,
        },
        '&.Mui-focused fieldset': {
          borderColor: theme.palette.primary.main,
        },
      },
    },
    buttonRow: {
      mt: 4,
      display: 'flex',
      justifyContent: 'flex-end',
      gap: 2,
    },
    submitButton: {
      borderRadius: 2,
      textTransform: 'none',
      fontWeight: 600,
      px: 4,
      py: 1.5,
      background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
      color: '#fff',
      boxShadow: '0 2px 8px rgba(25,118,210,0.08)',
      '&:hover': {
        background: `linear-gradient(90deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
      },
    },
    cancelButton: {
      borderRadius: 2,
      textTransform: 'none',
      fontWeight: 600,
      px: 4,
      py: 1.5,
      borderColor: theme.palette.grey[300],
      color: theme.palette.text.primary,
      background: '#f5f5f5',
      '&:hover': {
        borderColor: theme.palette.grey[500],
        backgroundColor: alpha(theme.palette.grey[400], 0.15),
      },
    },
    background: {
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #e3f0ff 0%, #f8fafd 100%)',
      py: 6,
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      mb: 2,
      color: theme.palette.primary.main,
      fontWeight: 700,
      fontSize: 28,
      letterSpacing: 1,
      justifyContent: 'center',
    },
    divider: {
      my: 2,
      borderColor: theme.palette.grey[200],
    },
  };

  useEffect(() => {
    if (!user || !(user.role === "user" || user.role === "admin")) {
      navigate("/");
      return;
    }
    fetchTypes();
    fetchUsers();
    fetchClients();
    // eslint-disable-next-line
  }, []);

  const fetchClients = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/clients`);
      setClientOptions(Array.isArray(res.data) ? res.data : []);
    } catch {
      setClientOptions([]);
    }
  };

  const fetchTypes = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/codes`);
      setTypeOptions(res.data);
    } catch {
      setTypeOptions([]);
    }
  };

  /* const fetchUsers = async () => {
    try {
      const res = await axios.get("http://localhost:3001/api/users");
      setUserOptions(res.data);
      console.log("Fetched users:", res.data);
    } catch {
      setUserOptions([]);
      console.log("Failed to fetch users");
    }
  }; */

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/users`);
      setUserOptions(res.data);
      console.log("Fetched users:", res.data);
    } catch {
      setUserOptions([]);
      console.log("Failed to fetch users");
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!form.client_name.trim()) errors.client_name = "Client name is required";
    if (!form.type_of_job) errors.type_of_job = "Type of job is required";
    if (!form.assigned_to) errors.assigned_to = "Assigned to is required";
    if (!form.job_description.trim()) errors.job_description = "Job description is required";
    if (!form.target_completion_date) errors.target_completion_date = "Target completion date is required";
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    // Clear validation error when user starts typing
    if (validationErrors[name]) {
      setValidationErrors(prev => ({ ...prev, [name]: "" }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    
    setError("");
    setSuccess("");
    setLoading(true);
    
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/api/jobs`, { ...form, firm });
      setSuccess("Job created successfully!");
      setTimeout(() => {
        if (user && user.role === "admin") {
          navigate("/admin");
        } else {
          navigate("/user", { state: { user } });
        }
      }, 1500);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to create job");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper elevation={0} sx={formStyles.paper}>
        <Box sx={formStyles.header}>
          <AddCircleOutline sx={{ fontSize: 32 }} />
          <Typography variant="h4" component="h1">
          Create New Job
        </Typography>
        </Box>

        {error && (
          <Alert 
            severity="error" 
            sx={{ 
              mb: 3,
              borderRadius: 1,
              '& .MuiAlert-icon': {
                color: theme.palette.error.main
              }
            }}
          >
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert 
            severity="success" 
            sx={{ 
              mb: 3,
              borderRadius: 1,
              '& .MuiAlert-icon': {
                color: theme.palette.success.main
              }
            }}
          >
            {success}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Typography variant="h6" sx={formStyles.sectionTitle}>
            Client Information
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Autocomplete
                freeSolo
                options={clientOptions
                  .filter(c => (c.firm || 'Datachef') === firm)
                  .map(c => c.name)}
                value={form.client_name}
                onInputChange={(e, val) => {
                  setForm(prev => ({ ...prev, client_name: val }));
                  if (validationErrors.client_name) {
                    setValidationErrors(prev => ({ ...prev, client_name: "" }));
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Client Name"
                    required
                    error={!!validationErrors.client_name}
                    helperText={validationErrors.client_name || "Pick from Masters or type a new client"}
                    sx={formStyles.field}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <InputAdornment position="start"><Person color="primary" /></InputAdornment>
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Grid>
          </Grid>

          <Typography variant="h6" sx={formStyles.sectionTitle}>
            Job Details
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField select label="Firm" name="firm" value={firm} onChange={(e) => setFirm(e.target.value)} InputLabelProps={{ shrink: true }} fullWidth sx={{ minWidth: 300 }} helperText={`Job ID prefix: ${firm === 'Techsahyogi' ? 'TS-' : 'DC-'}###`}>
                {FIRMS.map((f) => (
                  <MenuItem key={f} value={f}>{f}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Job Received Date" name="job_received_date" type="date" value={form.job_received_date} onChange={handleChange} InputLabelProps={{ shrink: true }} fullWidth required sx={{ minWidth: 300 }}/>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField select label="Type of Job" name="type_of_job" value={form.type_of_job} onChange={handleChange} InputLabelProps={{ shrink: true }} fullWidth required error={!!validationErrors.type_of_job} helperText={validationErrors.type_of_job} sx={{ minWidth: 300 }}>
                {typeOptions.map((option) => (
                  <MenuItem key={option.id} value={option.label}>{option.label}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                select
                label="Assigned To"
                name="assigned_to"
                value={form.assigned_to}
                onChange={handleChange}
                fullWidth
                required
                error={!!validationErrors.assigned_to}
                helperText={validationErrors.assigned_to}
                sx={{ minWidth: 300 }}
              >
                <MenuItem value="">Select Employee</MenuItem>
                {userOptions.map((user) => (
                  <MenuItem key={user.id} value={user.name}>{user.name}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField label="Target Completion Date" name="target_completion_date" type="date" value={form.target_completion_date} onChange={handleChange} InputLabelProps={{ shrink: true }} fullWidth required error={!!validationErrors.target_completion_date} helperText={validationErrors.target_completion_date} sx={{ minWidth: 300 }}/>
            </Grid>
            <Grid item xs={12}>
              <TextField label="Job Description" name="job_description" value={form.job_description} onChange={handleChange} InputLabelProps={{ shrink: true }} fullWidth multiline rows={3} required error={!!validationErrors.job_description} helperText={validationErrors.job_description} sx={{ minWidth: 300 }}/>
            </Grid>
          </Grid>

          <Divider sx={{ my: 4 }} />

          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            gap: 2 
          }}>
            <Button
              variant="outlined"
              color="secondary"
              onClick={() => navigate("/user", { state: { user } })}
              sx={{ ...formStyles.button, ...formStyles.cancelButton }}
              startIcon={<ArrowBack />}
            >
              Back to Dashboard
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={loading}
              sx={{ ...formStyles.button, ...formStyles.submitButton }}
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <AddCircleOutline />}
            >
              {loading ? "Creating..." : "Create Job"}
          </Button>
          </Box>
        </form>
      </Paper>
    </Container>
  );
};

export default JobEntryForm;